import { useState, useEffect } from 'react';
import { Table, Input, Row, Col, Spinner } from 'reactstrap';
import { Edit, Trash2, Plus, RefreshCw, Shield } from 'react-feather';
import { toast } from 'react-toastify';
import { Btn } from '../../../AbstractElements';
import RoleService from '../../../Api/services/role.service';
import type { components } from '../../../Api/types/openapi';
import RoleFormModal from './RoleFormModal';
import RolePermissionsModal from './RolePermissionsModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import { useTranslation } from 'react-i18next';

type RoleResponse = components['schemas']['RoleResponse'];
type PaginationRoleResponse = components['schemas']['Pagination_RoleResponse_'];

const RolesList = () => {
  const { t } = useTranslation();
  const [roles, setRoles] = useState<RoleResponse[]>([]);
  const [pagination, setPagination] = useState<PaginationRoleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RoleResponse | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    loadRoles();
  }, [currentPage, searchTerm]);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        limit,
      };

      if (searchTerm) {
        params.name = searchTerm;
      }

      const data = await RoleService.getRolesPaginated(params);
      
      if (!data || !Array.isArray(data.results)) {
        console.warn('Respuesta inválida del servidor: results no es un array');
        setRoles([]);
        setPagination(null);
        return;
      }
      
      setRoles(data.results);
      setPagination(data);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Error al cargar roles';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedRole(null);
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEdit = (role: RoleResponse) => {
    setSelectedRole(role);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleManagePermissions = (role: RoleResponse) => {
    setSelectedRole(role);
    setShowPermissionsModal(true);
  };

  const handleDelete = async (role: RoleResponse) => {
    if (!role.id) return;
    
    try {
      // Verificar si se puede eliminar
      await RoleService.checkDeleteRole(role.id);
      setSelectedRole(role);
      setShowDeleteModal(true);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'No se puede eliminar este rol';
      toast.error(errorMessage);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRole?.id) return;

    try {
      await RoleService.deleteRole(selectedRole.id);
      toast.success(t('Rol eliminado exitosamente'));
      setShowDeleteModal(false);
      setSelectedRole(null);
      loadRoles();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Error al eliminar rol';
      toast.error(errorMessage);
    }
  };

  const handleToggleState = async (role: RoleResponse) => {
    if (!role.id) return;

    try {
      await RoleService.changeRoleState(role.id);
      toast.success(t('Estado del rol actualizado'));
      loadRoles();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Error al cambiar estado';
      toast.error(errorMessage);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div>
      <Row className="mb-3">
        <Col md="6">
          <Input
            type="text"
            placeholder={t('Buscar por nombre...')}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </Col>
        <Col md="6" className="text-end">
          <Btn color="primary" onClick={handleCreate} className="me-2">
            <Plus size={16} className="me-1" />
            {t('Nuevo Rol')}
          </Btn>
          <Btn color="secondary" onClick={loadRoles}>
            <RefreshCw size={16} className="me-1" />
            {t('Actualizar')}
          </Btn>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
        </div>
      ) : (
        <>
          <Table responsive striped>
            <thead>
              <tr>
                <th>{t('ID')}</th>
                <th>{t('Nombre')}</th>
                <th>{t('Permisos')}</th>
                <th>{t('Público')}</th>
                <th>{t('Estado')}</th>
                <th>{t('Acciones')}</th>
              </tr>
            </thead>
            <tbody>
              {roles.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    {t('No se encontraron roles')}
                  </td>
                </tr>
              ) : (
                roles.map((role) => (
                  <tr key={role.id}>
                    <td>{role.id}</td>
                    <td>{role.name}</td>
                    <td>
                      <Btn
                        color="info"
                        size="sm"
                        onClick={() => handleManagePermissions(role)}
                      >
                        <Shield size={14} className="me-1" />
                        {role.permissions?.length || 0} {t('permisos')}
                      </Btn>
                    </td>
                    <td>
                      <span className={`badge ${role.is_public ? 'bg-success' : 'bg-secondary'}`}>
                        {role.is_public ? t('Sí') : t('No')}
                      </span>
                    </td>
                    <td>
                      <div className="form-check form-switch">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          checked={role.active}
                          onChange={() => handleToggleState(role)}
                        />
                      </div>
                    </td>
                    <td>
                      <Btn
                        color="primary"
                        size="sm"
                        className="me-1 btn_list_action"
                        onClick={() => handleEdit(role)}
                      >
                        <Edit size={14} />
                      </Btn>
                      <Btn
                        color="danger"
                        size="sm"
                        className="btn_list_action"
                        onClick={() => handleDelete(role)}
                      >
                        <Trash2 size={14} />
                      </Btn>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {pagination && pagination.pages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                {t('Página')} {pagination.current_page} {t('de')} {pagination.pages}
              </div>
              <div>
                <Btn
                  color="primary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  className="me-2"
                >
                  {t('Anterior')}
                </Btn>
                <Btn
                  color="primary"
                  size="sm"
                  disabled={currentPage === pagination.pages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  {t('Siguiente')}
                </Btn>
              </div>
            </div>
          )}
        </>
      )}

      <RoleFormModal
        isOpen={showModal}
        toggle={() => setShowModal(false)}
        role={selectedRole}
        isEditing={isEditing}
        onSuccess={loadRoles}
      />

      <RolePermissionsModal
        isOpen={showPermissionsModal}
        toggle={() => setShowPermissionsModal(false)}
        role={selectedRole}
        onSuccess={loadRoles}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        toggle={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('Eliminar Rol')}
        message={t('¿Estás seguro de que deseas eliminar este rol?')}
      />
    </div>
  );
};

export default RolesList;

